/**
 * A capture, opened at the size it was taken.
 *
 * Every screenshot in this manual is drawn at its capture size and capped by
 * the column, so the wide ones — a whole window, a settings panel, a workflow
 * graph — are shown at about two thirds of what they are. That is the right
 * default for reading past them and the wrong one for looking *at* one, and
 * until this there was no way to do the second short of opening the file.
 *
 * Only the images the column actually shrank get the cursor and the click. A
 * capture already at full size has nothing to open, and offering it anyway
 * teaches the reader that the pointer means nothing.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';

export interface Shot {
  src: string;
  alt: string;
  /** The capture's own width in CSS pixels — half the file, as the build wrote it. */
  width: number;
  /** The caption under the capture in the chapter, if it has one. */
  caption: string;
}

/**
 * The capture at 1:1 on a dark veil, scrolled inside its
 * frame when it is larger than the window. A click outside it, Close, or Esc
 * closes it; the page behind does not scroll.
 */
export function Lightbox({ shot, onClose }: { shot: Shot; onClose: () => void }): React.JSX.Element {
  const close = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', key);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const before = document.activeElement as HTMLElement | null;
    close.current?.focus();
    return () => {
      window.removeEventListener('keydown', key);
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, [onClose]);

  const label = shot.caption || shot.alt || 'Screenshot';
  const stop = (event: React.MouseEvent): void => event.stopPropagation();

  return (
    <div role="dialog" aria-modal="true" aria-label={label} className="al-lightbox" onClick={onClose}>
      <button ref={close} type="button" className="al-lightbox-close al-press" title="Close (Esc)" onClick={onClose}>
        Close <span className="al-lightbox-esc">ESC</span>
      </button>
      <div className="al-lightbox-frame" onClick={stop}>
        <img src={shot.src} alt={shot.alt} width={shot.width} style={{ width: shot.width }} />
      </div>
      <div className="al-lightbox-caption" onClick={stop}>
        {label}
        <span className="al-lightbox-scale">1:1</span>
      </div>
    </div>
  );
}

/**
 * Which captures the column shrank, marked so they can be clicked.
 *
 * Measured rather than assumed: the same image is full size on a wide window
 * and two thirds on a narrow one, and the answer changes when the window is
 * resized without the page being reloaded.
 */
export function useZoomableShots(
  prose: React.RefObject<HTMLElement | null>,
  html: string
): { shot: Shot | null; close: () => void } {
  const [shot, setShot] = useState<Shot | null>(null);

  useEffect(() => {
    const root = prose.current;
    if (!root) return;

    /** The capture's own width — what the build wrote — against what is drawn. */
    const shrunk = (img: HTMLImageElement): boolean => {
      const full = Number(img.getAttribute('width')) || img.naturalWidth;
      return full > Math.round(img.getBoundingClientRect().width) + 1;
    };

    const images = (): HTMLImageElement[] => [...root.querySelectorAll<HTMLImageElement>('img.al-shot')];
    // Best effort, and only the look: React owns this subtree's HTML, so a
    // class set here does not survive it rewriting the prose. The click below
    // measures for itself rather than trusting anything left behind.
    const mark = (): void => {
      for (const img of images()) {
        const zoomable = shrunk(img);
        img.classList.toggle('is-zoomable', zoomable);
        if (zoomable) img.setAttribute('title', 'Click to enlarge');
        else img.removeAttribute('title');
      }
    };

    const open = (event: MouseEvent): void => {
      const img = event.target as HTMLImageElement;
      if (img.tagName !== 'IMG' || !shrunk(img)) return;
      setShot({
        src: img.currentSrc || img.src,
        alt: img.alt,
        width: Number(img.getAttribute('width')) || img.naturalWidth,
        caption: img.closest('figure')?.querySelector('figcaption')?.textContent ?? ''
      });
    };

    mark();
    root.addEventListener('click', open);
    window.addEventListener('resize', mark);
    // A lazily-loaded capture has no size until it arrives.
    for (const img of images()) img.addEventListener('load', mark);
    return () => {
      root.removeEventListener('click', open);
      window.removeEventListener('resize', mark);
      for (const img of images()) img.removeEventListener('load', mark);
    };
  }, [prose, html]);

  // A chapter change closes whatever was open over the one before it.
  useEffect(() => setShot(null), [html]);

  return { shot, close: () => setShot(null) };
}
